/**
 * State machines — plan §4.4, as amended by addendum §E.2 and §B.1.
 *
 * Enforcement is belt-and-braces:
 *   1. the service layer calls `assertTransition()` before writing, and
 *   2. a `BEFORE UPDATE` trigger per stateful table rejects any pair that is not
 *      in the `allowed_transitions` table (seeded from `ALLOWED_TRANSITIONS`).
 *
 * CHECK constraints cannot see the old row, which is why the trigger exists.
 * Every accepted transition also writes a `<table>_status_history` row.
 */
import { ApiError } from './errors.js';
/** Addendum §B.1: one status per job. Billing and collection are trackers. */
export const JOB_STATUSES = [
    'requested',
    'quoted',
    /* Approved — renamed by migration 0162 (job-stages.md §9.1). */
    'approved',
    'scheduled',
    'in_progress',
    'completed',
    'invoiced',
    'paid',
    'declined',
    'cancelled',
    'written_off',
];
/**
 * The statuses nobody picks (`docs/job-stages.md` §2): each follows what happens to the job's
 * quotes and visits, or one of its actions. `POST /jobs/:id/transition` and the Jobs list's bulk
 * move refuse a move into any of them (`use_the_action`); a move into Cancelled runs Cancel job.
 */
export const JOB_STATUSES_BY_ACTION = [
    'requested', 'quoted', 'approved', 'scheduled', 'declined',
];
/*
 * `replaced` (0162, job-stages.md §9.5): a **draft** closed when another
 * version of the job's quote was approved. A sent or viewed quote that closes
 * is `expired`, with `closed_reason` saying why.
 */
export const QUOTE_STATUSES = ['draft', 'sent', 'viewed', 'approved', 'declined', 'expired', 'replaced'];
export const VISIT_STATUSES = [
    'planned', 'scheduled', 'dispatched', 'in_progress', 'completed', 'skipped', 'cancelled',
];
export const INVOICE_STATUSES = [
    'draft', 'awaiting_approval', 'scheduled', 'sent', 'viewed', 'partially_paid', 'paid', 'voided',
];
export const PO_STATUSES = [
    'draft', 'placed', 'ready_for_pickup', 'delivered', 'picked_up', 'receipt_verified', 'synced', 'cancelled',
];
/*
 * `retired` is here because `ALLOWED_TRANSITIONS` below can reach it from
 * four other states and `EQUIPMENT_STATUS` offers the word — it was the one
 * status reachable by transition that the enum did not name, which is how it
 * came to have no chip pair and no dot on a mark either.
 */
export const EQUIPMENT_STATUSES = [
    'available', 'assigned', 'due_for_service', 'in_shop', 'out_of_service', 'retired',
];
/**
 * The full transition table. Seeded into `allowed_transitions` by a migration,
 * so the database and the service layer can never disagree.
 */
export const ALLOWED_TRANSITIONS = {
    /*
     * The first half — Requested → Quoted → Approved → Scheduled, Declined,
     * Cancelled — is job-stages.md §9.2, exactly. None of these is a move a
     * person picks: each follows what happens to the job's quotes and visits
     * (§7) or one of its actions (§5), and `POST /jobs/:id/transition` refuses
     * a move into any of them but Cancelled (`use_the_action`).
     */
    job: [
        { from: 'requested', to: 'quoted' },
        { from: 'requested', to: 'cancelled', requiresReason: true },
        { from: 'quoted', to: 'approved' },
        { from: 'quoted', to: 'declined', requiresReason: true },
        // Withdraw quote, or its only open quote expired.
        { from: 'quoted', to: 'requested', requiresReason: true },
        { from: 'quoted', to: 'cancelled', requiresReason: true },
        // The first visit gets a day.
        { from: 'approved', to: 'scheduled' },
        { from: 'approved', to: 'cancelled', requiresReason: true },
        // Every booked day taken off again, and nothing started.
        { from: 'scheduled', to: 'approved' },
        { from: 'scheduled', to: 'in_progress' },
        { from: 'scheduled', to: 'completed' },
        { from: 'scheduled', to: 'cancelled', requiresReason: true },
        // A quote sent again · Reopen request · Cancel job.
        { from: 'declined', to: 'quoted' },
        { from: 'declined', to: 'requested' },
        { from: 'declined', to: 'cancelled', requiresReason: true },
        // Reopen request — only when no quote on the job was ever approved (§2).
        { from: 'cancelled', to: 'requested' },
        { from: 'in_progress', to: 'completed' },
        { from: 'in_progress', to: 'cancelled', requiresReason: true },
        // Addendum §B.1: computed from the trackers on every money event.
        // A job fully paid at completion goes straight to paid; invoiced is skipped.
        { from: 'completed', to: 'invoiced' },
        { from: 'completed', to: 'paid' },
        { from: 'completed', to: 'written_off', requiresReason: true },
        { from: 'invoiced', to: 'paid' },
        { from: 'invoiced', to: 'written_off', requiresReason: true },
        // Money events can move a job backwards: a void or a reversed payment
        // re-opens it. These are computed, never hand-picked.
        { from: 'invoiced', to: 'completed' },
        { from: 'paid', to: 'invoiced' },
        /*
         * And a job can be paid and *then* written off.
         *
         * §4.4 says "Written off (from Invoiced)", which is the shape of a job
         * with one invoice. A job with two — progress billing, or a recurring
         * contract — can have the first one paid, which computes the job to Paid,
         * and the second one written off months later. The trackers then say
         * balance zero and written-off over the threshold, which is Written off,
         * and the job was Paid a moment ago.
         *
         * Computed like the two above it, so it carries the same reason
         * requirement the other two write-off edges do.
         */
        { from: 'paid', to: 'written_off', requiresReason: true },
    ],
    quote: [
        { from: 'draft', to: 'sent' },
        { from: 'sent', to: 'viewed' },
        { from: 'sent', to: 'approved' },
        { from: 'sent', to: 'declined', requiresReason: true },
        { from: 'sent', to: 'expired' },
        { from: 'viewed', to: 'approved' },
        { from: 'viewed', to: 'declined', requiresReason: true },
        { from: 'viewed', to: 'expired' },
        // A draft closed when another version was approved (0162). No edge leaves it.
        { from: 'draft', to: 'replaced' },
    ],
    visit: [
        { from: 'planned', to: 'scheduled' },
        { from: 'planned', to: 'cancelled', requiresReason: true },
        { from: 'scheduled', to: 'dispatched' },
        { from: 'scheduled', to: 'skipped', requiresReason: true },
        { from: 'scheduled', to: 'cancelled', requiresReason: true },
        { from: 'scheduled', to: 'planned' }, // unscheduled again
        { from: 'dispatched', to: 'in_progress' },
        { from: 'dispatched', to: 'skipped', requiresReason: true },
        { from: 'in_progress', to: 'completed' },
        { from: 'in_progress', to: 'skipped', requiresReason: true },
        { from: 'completed', to: 'in_progress' }, // rework reopens the visit
    ],
    invoice: [
        { from: 'draft', to: 'awaiting_approval' },
        { from: 'draft', to: 'scheduled' },
        { from: 'draft', to: 'sent' },
        { from: 'draft', to: 'voided', requiresReason: true },
        { from: 'awaiting_approval', to: 'scheduled' },
        { from: 'awaiting_approval', to: 'sent' },
        { from: 'awaiting_approval', to: 'draft' },
        { from: 'awaiting_approval', to: 'voided', requiresReason: true },
        { from: 'scheduled', to: 'sent' },
        { from: 'scheduled', to: 'draft' },
        { from: 'scheduled', to: 'voided', requiresReason: true },
        { from: 'sent', to: 'viewed' },
        { from: 'sent', to: 'partially_paid' },
        { from: 'sent', to: 'paid' },
        { from: 'sent', to: 'voided', requiresReason: true },
        { from: 'viewed', to: 'partially_paid' },
        { from: 'viewed', to: 'paid' },
        { from: 'viewed', to: 'voided', requiresReason: true },
        { from: 'partially_paid', to: 'paid' },
        { from: 'partially_paid', to: 'voided', requiresReason: true },
        { from: 'paid', to: 'partially_paid' }, // a partial refund
        { from: 'paid', to: 'voided', requiresReason: true },
        /*
         * All the way back to owing the whole thing. §F.3 re-opens an invoice on a
         * returned ACH, a lost dispute and a full refund, and the machine has to
         * be able to say so: without these the reversal succeeds, the ledger is
         * right, and the status update is refused — leaving an invoice marked Paid
         * that nobody is owed money on.
         */
        { from: 'paid', to: 'sent' },
        { from: 'paid', to: 'viewed' },
        { from: 'partially_paid', to: 'sent' },
        { from: 'partially_paid', to: 'viewed' },
    ],
    purchase_order: [
        { from: 'draft', to: 'placed' },
        { from: 'draft', to: 'cancelled', requiresReason: true },
        { from: 'placed', to: 'ready_for_pickup' },
        { from: 'placed', to: 'delivered' },
        { from: 'placed', to: 'cancelled', requiresReason: true },
        { from: 'ready_for_pickup', to: 'picked_up' },
        { from: 'picked_up', to: 'receipt_verified' },
        { from: 'delivered', to: 'receipt_verified' },
        { from: 'receipt_verified', to: 'synced' },
    ],
    /*
     * Four states, not six. "Assigned today" and "Due for service" were states
     * here once; they are facts derived from today's assignments and from the
     * meter against the schedule, and storing them meant a truck assigned
     * yesterday and never reset still read as assigned this morning. Phase 6
     * narrowed the column to what a person actually sets (§6.8).
     *
     * Going out of service needs a reason — dispatch has to be told why a truck
     * cannot go out. Coming back needs none: the closed work order is the
     * record, and asking for a second one just gets "fixed" typed forty times.
     */
    equipment: [
        { from: 'available', to: 'in_shop' },
        { from: 'available', to: 'out_of_service', requiresReason: true },
        { from: 'available', to: 'retired', requiresReason: true },
        { from: 'in_shop', to: 'available' },
        { from: 'in_shop', to: 'out_of_service', requiresReason: true },
        { from: 'in_shop', to: 'retired', requiresReason: true },
        { from: 'out_of_service', to: 'available' },
        { from: 'out_of_service', to: 'in_shop' },
        { from: 'out_of_service', to: 'retired', requiresReason: true },
        { from: 'retired', to: 'available', requiresReason: true },
    ],
};
export function findTransition(entity, from, to) {
    return ALLOWED_TRANSITIONS[entity].find((t) => t.from === from && t.to === to);
}
export function canTransition(entity, from, to) {
    return from === to || findTransition(entity, from, to) !== undefined;
}
/**
 * Throws unless the move is legal and carries a reason where one is required.
 * The database trigger repeats this check; both must pass.
 */
export function assertTransition(entity, from, to, reason) {
    if (from === to)
        return;
    const transition = findTransition(entity, from, to);
    if (!transition) {
        throw new ApiError('invalid_transition', `A ${entity} cannot go from ${from} to ${to}.`, {
            details: { entity, from, to },
        });
    }
    if (transition.requiresReason && !reason?.trim()) {
        throw new ApiError('validation_failed', `Moving a ${entity} to ${to} needs a reason.`, {
            field: 'reason',
            details: { entity, from, to },
        });
    }
}
//# sourceMappingURL=state-machines.js.map